import {
    Component,
    OnInit,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Subject, firstValueFrom, merge } from 'rxjs';
import * as ExcelJS from 'exceljs';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import {
    MatTableModule,
    MatTableDataSource,
    MatTable,
} from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { TransportOrderDetailComponent } from './detalle-orden-transporte/transport-order-detail.component';

// Enum usado para compatibilidad (ya no se usa pero se mantiene por si acaso)
// eslint-disable-next-line no-unused-vars
export enum OsAgenciaTabType {
    ORDER_SERVICES = 'ORDER_SERVICES',
    TRANSPORT = 'TRANSPORT',
    ADDITIONAL_COST = 'ADDITIONAL_COST',
}

// Servicios
import { OrdenesTransporteService } from '../../../services/ordenes-transporte.service';
import { CommonsService } from '../../../services/commons.service';
import { ClientService, Client } from '../../../services/client.service';
import { IVesselFilter } from '../../../services/os-agencia.service';
import {
    LiquidacionesService,
    TemporadaDto,
} from '../../../services/liquidaciones.service';
import { LoggerService } from '../../../core/services/logger.service';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import { TransportOrdersStateService } from '../../../services/transport-orders-state.service';
import {
    formatDateForApi,
    getDefaultDateRange,
} from '../../../core/utils/date.utils';
import { TransportOrdersNavigationService } from '../../../services/transport-orders-navigation.service';
import {
    OrdenesTransporteFilterDto,
    OrdenesTransporteResponseDto,
    OrdenesTransporteItemDto,
    OrdenesTransporteConsolidadoItemDto,
    ExportadorTransporteComboDto,
    NaveTransporteComboDto,
    TemporadaTransporteComboDto,
} from '@opb-workspace/shared';

/**
 * Interfaz local para gestión de datos en el componente
 */
interface OrdenTransporteAgenciaLocal {
    // Campos del DTO
    nroOt: number;
    nroLiquidacion?: number;
    tipoEmbarque?: string;
    cliente?: string;
    clienteId?: number;
    exportador?: string;
    servicio?: string;
    nave?: string;
    etd?: string;
    refCliente?: string;
    booking?: string;
    montoNeto?: string;
    moneda?: string;
    extraCostos?: string;
    extraCostosMoneda?: string;
    subTotal?: string;
    // Campos calculados localmente
    numeroLiquidacion?: string;
}

/**
 * Interfaz para un total individual por moneda
 */
interface TotalPorMoneda {
    moneda: string;
    totalMontoNeto: string;
    totalExtraCostos: string;
    totalSubTotal: string;
}

/**
 * Interfaz para totales por tab
 */
interface OrdenTransporteAgenciaTotales {
    count: number;
    totalMontoNeto: string;
    totalExtraCostos: string;
    totalSubTotal: string;
    hasMixedCurrencies: boolean;
    totalesPorMoneda: TotalPorMoneda[];
}

interface OrdenTransporteDetalleState {
    isLoading: boolean;
    data?: any; // TODO: Definir interfaz de detalle si es necesario
    error?: string;
}

import { Router, ActivatedRoute } from '@angular/router'; // Add Router imports

@Component({
    selector: 'app-ordenes-transporte-agencia',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        MatChipsModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatDialogModule, // Add Dialog Module
    ],
    templateUrl: './ordenes-transporte-agencia.component.html',
    styleUrls: ['./ordenes-transporte-agencia.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OrdenesTransporteAgenciaComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private destroy$ = new Subject<void>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<OrdenTransporteAgenciaLocal>;

    // Tipo de tab fijo para órdenes de transporte
    currentTabType = OsAgenciaTabType.TRANSPORT;

    // Columnas de la tabla (se configura dinámicamente según rol)
    displayedColumns: string[] = [];
    private isClientRole = false;

    // Datos de la tabla
    dataSource = new MatTableDataSource<OrdenTransporteAgenciaLocal>([]);
    expandedRowKey: string | null = null;
    orderDetails: Record<string, OrdenTransporteDetalleState | undefined> = {};

    // Estados de carga
    isLoading = false;
    hasError = false;
    errorMessage = '';
    isGeneratingExcel = false;
    isGeneratingExcelConsolidado = false;

    // Datos de paginación y totales
    totalRegistros = 0;
    totalPaginas = 0;
    paginaActual = 1;
    elementosPorPagina = 10;

    // Totales por tab
    totales: OrdenTransporteAgenciaTotales = {
        count: 0,
        totalMontoNeto: '—',
        totalExtraCostos: '—',
        totalSubTotal: '—',
        hasMixedCurrencies: false,
        totalesPorMoneda: [],
    };

    // Filtros según imagen
    filtroCliente = new FormControl<string | number | null>('');
    filtroExportador = new FormControl<string | number | null>('');
    filtroNave = new FormControl<string | number | null>('');
    filtroTemporada = new FormControl<string | null>(null);
    filtroInicio = new FormControl<Date | null>(null);
    filtroFin = new FormControl<Date | null>(null);
    filtroRefCliente = new FormControl('');
    filtroContenedor = new FormControl('');
    filtroDus = new FormControl('');
    filtroNroLiquidacion = new FormControl('');
    filtroBooking = new FormControl('');
    filtroPatente = new FormControl('');

    // Opciones para selectores (se cargarán dinámicamente)
    clientesOptions: Client[] = [];
    filteredClientesOptions: Client[] = [];
    exportadoresOptions: { exportadorId: number; exportador: string }[] = [];
    filteredExportadoresOptions: {
        exportadorId: number;
        exportador: string;
    }[] = [];
    navesOptions: IVesselFilter[] = [];
    filteredNavesOptions: IVesselFilter[] = [];
    temporadasOptions: { value: string; label: string }[] = [];
    temporadasData: TemporadaDto[] = []; // Almacenar datos completos de temporadas

    constructor(
        // eslint-disable-next-line no-unused-vars
        private ordenesTransporteService: OrdenesTransporteService,
        // eslint-disable-next-line no-unused-vars
        private commonsService: CommonsService,
        // eslint-disable-next-line no-unused-vars
        private clientService: ClientService,
        // eslint-disable-next-line no-unused-vars
        private liquidacionesService: LiquidacionesService,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private authService: AuthService,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        private router: Router,
        private route: ActivatedRoute,
        private stateService: TransportOrdersStateService,
        private navigationService: TransportOrdersNavigationService
    ) {}

    ngOnInit(): void {
        this.setupDateLockBehavior();
        this.logger.info(
            '🔄 OrdenesTransporteAgenciaComponent: Inicializando componente'
        );
        this.isClientRole = this.authService.hasRole('client');
        this.configurarColumnas();
        this.setupFilters();
        this.setupClientFilter();

        // 1. Cargar estado desde URL si existe
        const stateLoaded = this.navigationService.loadStateFromUrl(this.route);
        if (stateLoaded) {
            this.logger.info(
                '✅ Estado cargado desde URL',
                'OrdenesTransporteAgenciaComponent'
            );
        }

        // 2. Cargar datos maestros y luego aplicar estado y buscar
        this.loadInitialData().then(() => {
            // 3. Aplicar estado a los controles (ahora que tenemos los datos maestros)
            if (stateLoaded) {
                this.aplicarEstadoEnComponente();
            }

            // 3b. Si no hay fechas del state, aplicar rango por defecto
            if (!this.filtroInicio.value && !this.filtroFin.value) {
                const { from, to } = getDefaultDateRange();
                this.filtroInicio.setValue(from);
                this.filtroFin.setValue(to);
            }

            // 4. Buscar órdenes
            this.buscarOrdenes();
        });
    }

    /**
     * Aplica el estado actual del servicio en los controles del componente
     */
    private aplicarEstadoEnComponente(): void {
        const state = this.stateService.getCurrentState();
        const filters = state.filters;

        this.logger.info(
            '📋 Aplicando estado en controles del componente',
            'OrdenesTransporteAgenciaComponent'
        );

        // Actualizar controles de formulario
        if (filters.clienteId) this.filtroCliente.setValue(filters.clienteId);
        if (filters.exportadorId)
            this.filtroExportador.setValue(filters.exportadorId);
        if (filters.nave) this.filtroNave.setValue(filters.nave);
        if (filters.temporadaId)
            this.filtroTemporada.setValue(filters.temporadaId.toString());

        if (filters.dateFrom)
            this.filtroInicio.setValue(new Date(filters.dateFrom));
        if (filters.dateTo) this.filtroFin.setValue(new Date(filters.dateTo));

        if (filters.referenciaCliente)
            this.filtroRefCliente.setValue(filters.referenciaCliente);
        if (filters.contenedor)
            this.filtroContenedor.setValue(filters.contenedor);
        if (filters.dus) this.filtroDus.setValue(filters.dus);
        if (filters.nroLiquidacion)
            this.filtroNroLiquidacion.setValue(
                filters.nroLiquidacion.toString()
            );
        if (filters.booking) this.filtroBooking.setValue(filters.booking);
        if (filters.licensePlate)
            this.filtroPatente.setValue(filters.licensePlate);

        // Actualizar paginación
        this.paginaActual = filters.page || 1;
        this.elementosPorPagina = filters.limit || 10;

        if (this.paginator) {
            this.paginator.pageIndex = this.paginaActual - 1;
            this.paginator.pageSize = this.elementosPorPagina;
        }

        // Actualizar ordenamiento
        if (this.sort && filters.orderBy) {
            this.sort.active = filters.orderBy;
            this.sort.direction =
                (filters.orderDirection?.toLowerCase() as 'asc' | 'desc') ||
                'desc';
        }
    }

    /** Configura columnas de la tabla según el rol del usuario */
    private configurarColumnas(): void {
        const base = [
            'nroOt',
            'nroLiquidacion',
            'tipoEmbarque',
            'exportador',
            'servicio',
            'nave',
            'etd',
            'refCliente',
            'booking',
            'montoNeto',
            'extraCostos',
            'subTotal',
            'acciones',
        ];
        this.displayedColumns = this.isClientRole
            ? base
            : [
                  'nroOt',
                  'nroLiquidacion',
                  'tipoEmbarque',
                  'cliente',
                  ...base.slice(3),
              ];
    }

    /**
     * Configura el filtro de cliente según el tipo de usuario
     * Similar a control-dus: muestra el nombre del cliente en lugar del ID
     */
    private setupClientFilter(): void {
        const currentUser = this.authService.getCurrentUser();

        if (currentUser?.roles.includes('client') && currentUser?.clientId) {
            // Si es usuario cliente, esperar a que se carguen los clientes para mostrar el nombre
            // El filtro se configurará en loadClients() después de cargar la lista
            this.logger.info(
                '👤 Usuario cliente detectado, se configurará el filtro después de cargar clientes'
            );
        } else {
            this.logger.info(
                '👤 Usuario no es cliente, filtro de cliente habilitado'
            );
        }
    }

    ngAfterViewInit(): void {
        // Configurar paginador y ordenamiento
        if (this.paginator && this.sort) {
            // Asignar el sort al dataSource
            this.dataSource.sort = this.sort;

            // Suscribirse a cambios de ordenamiento
            this.sort.sortChange.subscribe(() => {
                this.paginaActual = 1;
                if (this.paginator) {
                    this.paginator.pageIndex = 0;
                }

                // Actualizar estado de ordenamiento
                this.stateService.updateFilters({
                    orderBy: this.sort.active,
                    orderDirection: this.sort.direction.toUpperCase() as
                        | 'ASC'
                        | 'DESC',
                    page: 1,
                });

                this.navigationService.applyFilters();
                this.buscarOrdenes();
            });
        }
    }

    /**
     * Maneja el evento de cambio de página del paginador
     */
    onPageChange(event: any): void {
        this.logger.info(
            '📄 Cambio de página: ' +
                JSON.stringify({
                    pageIndex: event.pageIndex,
                    pageSize: event.pageSize,
                    previousPageIndex: event.previousPageIndex,
                    length: event.length,
                })
        );

        // Actualizar variables de paginación
        this.paginaActual = event.pageIndex + 1;
        this.elementosPorPagina = event.pageSize;

        // Actualizar estado
        this.stateService.updateFilters({
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        });

        this.navigationService.applyFilters();
        this.buscarOrdenes();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Configura los filtros para autocompletar (sin búsqueda automática)
     */
    private setupFilters(): void {
        // Suscribirse a los cambios solo para actualizar las opciones de autocompletar
        this.filtroCliente.valueChanges.subscribe((value) => {
            this.updateFilteredClientes(value);
        });
        this.filtroExportador.valueChanges.subscribe((value) => {
            this.updateFilteredExportadores(value);
        });
        this.filtroNave.valueChanges.subscribe((value) => {
            this.updateFilteredNaves(value);
        });
    }

    /**
     * Carga datos iniciales
     */
    private async loadInitialData(): Promise<void> {
        try {
            await Promise.all([
                this.loadVessels(),
                this.loadTemporadas(),
                this.loadExporters(),
                this.loadClients(),
            ]);
        } catch (error) {
            this.handleError('Error al cargar datos iniciales', error);
        }
    }

    /**
     * Carga la lista de clientes activos desde el endpoint /api/v1/clients/active
     */
    private async loadClients(): Promise<void> {
        try {
            this.logger.info(
                '🔄 Cargando clientes activos desde /api/v1/clients/active'
            );
            const response = await firstValueFrom(
                this.userService.getActiveClients()
            );

            if (response?.success && response.data) {
                this.clientesOptions = response.data;
                this.logger.info(
                    `✅ Cargados ${this.clientesOptions.length} clientes activos`
                );
                this.updateFilteredClientes(this.filtroCliente.value);

                // Si es usuario cliente, configurar el filtro con el nombre del cliente
                const currentUser = this.authService.getCurrentUser();
                if (
                    currentUser?.roles.includes('client') &&
                    currentUser?.clientId
                ) {
                    const clientIdNum = Number(currentUser.clientId);
                    const clienteEncontrado = this.clientesOptions.find(
                        (c) => c.clienteId === clientIdNum
                    );

                    if (clienteEncontrado) {
                        // Establecer el nombre del cliente en el filtro (no el ID)
                        this.filtroCliente.setValue(clienteEncontrado.cliente);
                        this.filtroCliente.disable();
                        this.logger.info(
                            `👤 Usuario cliente detectado, filtro configurado con nombre: ${clienteEncontrado.cliente}`
                        );
                    } else {
                        // Fallback si no se encuentra en la lista
                        this.filtroCliente.setValue(`Cliente #${clientIdNum}`);
                        this.filtroCliente.disable();
                        this.logger.warn(
                            `⚠️ Cliente #${clientIdNum} no encontrado en la lista, usando fallback`
                        );
                    }
                }
            } else {
                this.logger.warn('⚠️ No se recibieron datos de clientes');
                this.clientesOptions = [];
                this.filteredClientesOptions = [];
            }
        } catch (error) {
            this.handleError('Error al cargar clientes', error);
            this.clientesOptions = [];
            this.filteredClientesOptions = [];
        }
    }

    /**
     * Carga la lista de naves desde el endpoint de filtros de órdenes de transporte
     * Endpoint: GET /api/v1/ordenes-transporte/filters/vessels
     */
    private async loadVessels(): Promise<void> {
        try {
            this.logger.info(
                '🔄 Cargando naves desde /api/v1/ordenes-transporte/filters/vessels'
            );
            const response = await firstValueFrom(
                this.ordenesTransporteService.getVesselsCombo()
            );

            if (response?.data) {
                // Mapear NaveTransporteComboDto a IVesselFilter
                this.navesOptions = response.data.map(
                    (n: NaveTransporteComboDto) => ({
                        vesselId: n.vesselId,
                        vesselName: n.vesselName,
                    })
                );
                this.logger.info(
                    `✅ Cargadas ${this.navesOptions.length} naves desde órdenes de transporte`
                );
                this.updateFilteredNaves(this.filtroNave.value);
            } else {
                this.logger.warn('⚠️ No se recibieron datos de naves');
                this.navesOptions = [];
                this.filteredNavesOptions = [];
            }
        } catch (error) {
            this.handleError('Error al cargar naves', error);
            this.navesOptions = [];
            this.filteredNavesOptions = [];
        }
    }

    /**
     * Carga la lista de exportadores desde el endpoint de filtros de órdenes de transporte
     * Endpoint: GET /api/v1/ordenes-transporte/filters/exporters
     */
    private async loadExporters(): Promise<void> {
        try {
            this.logger.info(
                '🔄 Cargando exportadores desde /api/v1/ordenes-transporte/filters/exporters'
            );
            const response = await firstValueFrom(
                this.ordenesTransporteService.getExportersCombo()
            );

            if (response?.data) {
                // Mapear ExportadorTransporteComboDto a array con exportadorId y exportador
                this.exportadoresOptions = response.data
                    .map((e: ExportadorTransporteComboDto) => ({
                        exportadorId: e.exportadorId,
                        exportador: e.exportador,
                    }))
                    .sort((a, b) => a.exportador.localeCompare(b.exportador));
                this.logger.info(
                    `✅ Cargados ${this.exportadoresOptions.length} exportadores desde órdenes de transporte`
                );
                this.updateFilteredExportadores(this.filtroExportador.value);
            } else {
                this.logger.warn('⚠️ No se recibieron datos de exportadores');
                this.exportadoresOptions = [];
            }
        } catch (error) {
            this.handleError('Error al cargar exportadores', error);
            this.exportadoresOptions = [];
        }
    }

    /**
     * Carga la lista de temporadas desde el endpoint de filtros de órdenes de transporte
     * Endpoint: GET /api/v1/ordenes-transporte/filters/seasons
     */
    private async loadTemporadas(): Promise<void> {
        try {
            this.logger.info(
                '🔄 Cargando temporadas desde /api/v1/ordenes-transporte/filters/seasons'
            );
            const response = await firstValueFrom(
                this.ordenesTransporteService.getSeasonsCombo()
            );

            if (response?.data) {
                // Ordenar por seasonId descendente (más recientes primero)
                const temporadasOrdenadas = response.data
                    .slice()
                    .sort((a, b) => (b.seasonId || 0) - (a.seasonId || 0));

                // Mapear TemporadaTransporteComboDto a formato para el select
                this.temporadasOptions = temporadasOrdenadas.map(
                    (t: TemporadaTransporteComboDto) => ({
                        value: t.seasonId.toString(),
                        label: t.seasonName || `${t.seasonId}`,
                    })
                );

                // Mantener temporadasData para compatibilidad (mapear a TemporadaDto si se necesita)
                // Por ahora, solo guardamos los datos originales en un formato compatible
                this.temporadasData = temporadasOrdenadas.map(
                    (t: TemporadaTransporteComboDto) => ({
                        id: t.seasonId,
                        nombre: t.seasonName || '',
                        fechaInicio: t.seasonFrom || '',
                        fechaFin: t.seasonTo || '',
                        activa: 1, // 1 = activa, 0 = inactiva
                    })
                );

                this.logger.info(
                    `✅ Cargadas ${this.temporadasOptions.length} temporadas desde órdenes de transporte`
                );

                // Aplicar la última temporada por defecto (similar a liquidaciones VD)
                this.aplicarTemporadaPorDefecto();
            } else {
                this.logger.warn('⚠️ No se recibieron datos de temporadas');
                this.temporadasOptions = [];
                this.temporadasData = [];
            }
        } catch (error) {
            this.handleError('Error al cargar temporadas', error);
            this.temporadasOptions = [];
            this.temporadasData = [];
        }
    }

    /**
     * Formatea una temporada para mostrar
     */
    private formatearTemporada(t: TemporadaDto): string {
        const ini = new Date(t.fechaInicio).getFullYear();
        const fin = new Date(t.fechaFin).getFullYear();
        return `${ini} - ${fin}`;
    }

    /**
     * Aplica la última temporada por defecto (similar a liquidaciones VD)
     */
    private aplicarTemporadaPorDefecto(force = false): void {
        if (!this.temporadasData.length) {
            if (force) {
                this.filtroTemporada.setValue(null);
            }
            return;
        }
        if (!force && this.filtroTemporada.value) {
            return;
        }
        const ultimaTemporada = this.obtenerTemporadaMasReciente();
        if (ultimaTemporada) {
            this.filtroTemporada.setValue(ultimaTemporada.id.toString());
            this.logger.info(
                `✅ Temporada por defecto aplicada: ${ultimaTemporada.nombre} (ID: ${ultimaTemporada.id})`
            );
        } else {
            this.filtroTemporada.setValue(null);
        }
    }

    /**
     * Obtiene la temporada más reciente basándose en las fechas
     */
    private obtenerTemporadaMasReciente(): TemporadaDto | null {
        if (!this.temporadasData.length) {
            return null;
        }
        return this.temporadasData.reduce((prev, current) => {
            const prevDate = new Date(
                prev.fechaInicio ?? prev.fechaFin ?? ''
            ).getTime();
            const currentDate = new Date(
                current.fechaInicio ?? current.fechaFin ?? ''
            ).getTime();
            return currentDate >= prevDate ? current : prev;
        });
    }

    /**
     * Construye objeto de filtros basado en los controles del formulario
     */
    private buildFilters(): OrdenesTransporteFilterDto {
        const filters: OrdenesTransporteFilterDto = {};

        // Filtros según imagen
        // Si el filtro está deshabilitado (usuario cliente), usar el clientId del usuario directamente
        if (this.filtroCliente.disabled) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser?.clientId) {
                filters.clienteId = Number(currentUser.clientId);
            }
        } else if (
            this.filtroCliente.value !== null &&
            this.filtroCliente.value !== undefined &&
            this.filtroCliente.value !== ''
        ) {
            const raw = this.filtroCliente.value as unknown;

            // Si es un número, usar directamente
            if (typeof raw === 'number') {
                filters.clienteId = raw;
            } else if (typeof raw === 'string') {
                // Si es un string (nombre del cliente), buscar el clienteId correspondiente
                const clienteEncontrado = this.clientesOptions.find(
                    (c) => c.cliente === raw
                );
                if (clienteEncontrado) {
                    filters.clienteId = clienteEncontrado.clienteId;
                } else {
                    // Fallback: intentar parsear como número si es un ID
                    const clienteId = parseInt(String(raw), 10);
                    if (!isNaN(clienteId)) {
                        filters.clienteId = clienteId;
                    }
                }
            } else {
                // Fallback: intentar parsear como número
                const clienteId = parseInt(String(raw), 10);
                if (!isNaN(clienteId)) {
                    filters.clienteId = clienteId;
                }
            }
        }

        // Exportador
        if (this.filtroExportador.value) {
            // Si es un número (exportadorId), usar directamente
            if (typeof this.filtroExportador.value === 'number') {
                filters.exportadorId = this.filtroExportador.value;
            } else if (
                typeof this.filtroExportador.value === 'string' &&
                this.filtroExportador.value.trim()
            ) {
                // Si es un string (nombre del exportador), buscar el exportadorId correspondiente
                const exportadorEncontrado = this.exportadoresOptions.find(
                    (e) => e.exportador === this.filtroExportador.value
                );
                if (exportadorEncontrado) {
                    filters.exportadorId = exportadorEncontrado.exportadorId;
                } else {
                    // Fallback: intentar parsear como número si es un ID
                    const exportadorId = parseInt(
                        String(this.filtroExportador.value),
                        10
                    );
                    if (!isNaN(exportadorId)) {
                        filters.exportadorId = exportadorId;
                    }
                }
            }
        }

        if (this.filtroNave.value) {
            // Si es un número (vesselId), buscar el nombre de la nave
            if (typeof this.filtroNave.value === 'number') {
                const nave = this.navesOptions.find(
                    (n) => n.vesselId === this.filtroNave.value
                );
                if (nave?.vesselName) {
                    filters.nave = nave.vesselName;
                }
            } else if (
                typeof this.filtroNave.value === 'string' &&
                this.filtroNave.value.trim()
            ) {
                filters.nave = this.filtroNave.value.trim();
            }
        }

        // Temporada
        if (this.filtroTemporada.value) {
            const temporadaId = parseInt(this.filtroTemporada.value);
            if (!isNaN(temporadaId)) {
                filters.temporadaId = temporadaId;
            }
        }

        // Fechas manuales tienen prioridad sobre temporada
        if (this.filtroInicio.value && !this.filtroInicio.disabled) {
            filters.dateFrom = this.formatDate(this.filtroInicio.value);
        }

        if (this.filtroFin.value && !this.filtroFin.disabled) {
            filters.dateTo = this.formatDate(this.filtroFin.value);
        }

        if (this.filtroRefCliente.value?.trim()) {
            filters.referenciaCliente = this.filtroRefCliente.value.trim();
        }

        if (this.filtroContenedor.value?.trim()) {
            filters.contenedor = this.filtroContenedor.value.trim();
        }

        if (this.filtroDus.value?.trim()) {
            filters.dus = this.filtroDus.value.trim();
        }

        if (this.filtroNroLiquidacion.value?.trim()) {
            const liquidacionId = parseInt(
                this.filtroNroLiquidacion.value.trim()
            );
            if (!isNaN(liquidacionId)) {
                filters.nroLiquidacion = liquidacionId;
            }
        }

        if (this.filtroBooking.value?.trim()) {
            filters.booking = this.filtroBooking.value.trim();
        }

        if (this.filtroPatente.value?.trim()) {
            filters.licensePlate = this.filtroPatente.value.trim();
        }

        return filters;
    }

    /**
     * Actualiza la lista filtrada de exportadores para el autocompletar
     */
    private updateFilteredExportadores(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const found = this.exportadoresOptions.find(
                (e) => e.exportadorId === input
            );
            this.filteredExportadoresOptions = found
                ? [found]
                : this.exportadoresOptions.slice(0, 50);
            return;
        }
        if (term === '') {
            this.filteredExportadoresOptions = this.exportadoresOptions.slice(
                0,
                50
            );
            return;
        }
        this.filteredExportadoresOptions = this.exportadoresOptions
            .filter(
                (e) =>
                    e.exportador?.toLowerCase().includes(term) ||
                    String(e.exportadorId).includes(term)
            )
            .slice(0, 50);
    }

    /**
     * Mostrar nombre del exportador en el input del autocompletar
     */
    displayExportador = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.exportadoresOptions.find(
                (e) => e.exportadorId === value
            );
            return found ? found.exportador : String(value);
        }
        return '';
    };

    /**
     * Actualiza la lista filtrada de naves para el autocompletar
     */
    private updateFilteredNaves(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.navesOptions.find((n) => n.vesselId === input);
            this.filteredNavesOptions = match
                ? [match]
                : this.navesOptions.slice(0, 50);
            return;
        }
        if (term === '') {
            this.filteredNavesOptions = this.navesOptions.slice(0, 50);
            return;
        }
        this.filteredNavesOptions = this.navesOptions
            .filter(
                (n) =>
                    n.vesselName?.toLowerCase().includes(term) ||
                    String(n.vesselId).includes(term)
            )
            .slice(0, 50);
    }

    /**
     * Mostrar nombre de la nave en el input del autocompletar
     */
    displayNave = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.navesOptions.find((n) => n.vesselId === value);
            return found ? found.vesselName : String(value);
        }
        return '';
    };

    /**
     * Actualiza la lista filtrada de clientes para el autocompletar
     */
    private updateFilteredClientes(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.clientesOptions.find(
                (c) => c.clienteId === input
            );
            this.filteredClientesOptions = match
                ? [match]
                : this.clientesOptions.slice(0, 50);
            return;
        }
        if (term === '') {
            this.filteredClientesOptions = this.clientesOptions.slice(0, 50);
            return;
        }
        this.filteredClientesOptions = this.clientesOptions
            .filter(
                (c) =>
                    c.cliente?.toLowerCase().includes(term) ||
                    String(c.clienteId).includes(term)
            )
            .slice(0, 50);
    }

    /**
     * Mostrar nombre del cliente en el input del autocompletar
     */
    displayCliente = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.clientesOptions.find(
                (c) => c.clienteId === value
            );
            return found ? found.cliente : String(value);
        }
        return '';
    };

    get datesLockedBySearch(): boolean {
        return this.filtroInicio.disabled;
    }

    private setupDateLockBehavior(): void {
        merge(
            this.filtroContenedor.valueChanges,
            this.filtroDus.valueChanges,
            this.filtroNroLiquidacion.valueChanges,
            this.filtroBooking.valueChanges,
            this.filtroPatente.valueChanges,
        ).subscribe(() => {
            const hasValue =
                !!this.filtroContenedor.value?.trim() ||
                !!this.filtroDus.value?.trim() ||
                !!this.filtroNroLiquidacion.value?.trim() ||
                !!this.filtroBooking.value?.trim() ||
                !!this.filtroPatente.value?.trim();
            if (hasValue) {
                this.filtroInicio.setValue(null, { emitEvent: false });
                this.filtroFin.setValue(null, { emitEvent: false });
                this.filtroInicio.disable({ emitEvent: false });
                this.filtroFin.disable({ emitEvent: false });
            } else {
                this.filtroInicio.enable({ emitEvent: false });
                this.filtroFin.enable({ emitEvent: false });
            }
        });
    }

    /**
     * Aplica filtros y resetea el paginador a la primera página
     */
    aplicarFiltros(): void {
        this.paginaActual = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }

        // Actualizar estado con los nuevos filtros
        const filters = this.buildFilters();
        this.stateService.updateFilters({
            ...filters,
            page: 1,
        });

        this.navigationService.applyFilters();
        this.buscarOrdenes();
    }

    /**
     * Busca órdenes según filtros
     */
    async buscarOrdenes(): Promise<void> {
        this.isLoading = true;
        this.hasError = false;

        try {
            const filters = this.buildFilters();
            const orderBy = this.sort?.active || 'etd';
            const orderDirection =
                (this.sort?.direction.toUpperCase() as 'ASC' | 'DESC') ||
                'DESC';

            // Asegurarse de que los valores de paginación sean válidos
            const page = Math.max(1, this.paginaActual);
            const limit = Math.max(5, this.elementosPorPagina);

            const query: OrdenesTransporteFilterDto = {
                ...filters,
                page,
                limit,
                orderBy,
                orderDirection,
            };

            this.logger.info(
                '📋 Query de paginación: ' +
                    JSON.stringify({
                        page: query.page,
                        limit: query.limit,
                        total: this.totalRegistros,
                    })
            );

            this.logger.info(
                `📋 Buscando órdenes con filtros:`,
                JSON.stringify(query)
            );

            const response = await this.ordenesTransporteService
                .getOrdenes(query)
                .toPromise();

            if (response) {
                // Verificar si la respuesta es válida
                if (
                    response.pagination.page !== page ||
                    response.pagination.limit !== limit
                ) {
                    this.logger.warn(
                        '⚠️ Discrepancia en paginación: ' +
                            JSON.stringify({
                                requested: { page, limit },
                                received: {
                                    page: response.pagination.page,
                                    limit: response.pagination.limit,
                                },
                            })
                    );
                }

                this.processSearchResults(response);
            }
        } catch (error) {
            this.handleError('Error al buscar órdenes de transporte', error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Procesa los resultados de búsqueda
     */
    private processSearchResults(response: OrdenesTransporteResponseDto): void {
        try {
            // Mapear datos del DTO al formato local
            let mappedData: OrdenTransporteAgenciaLocal[] = response.data.map(
                (item) => ({
                    nroOt: item.nroOt,
                    nroLiquidacion: item.nroLiquidacion,
                    tipoEmbarque: item.tipoEmbarque,
                    cliente: item.cliente,
                    clienteId: item.clienteId,
                    exportador: item.exportador,
                    servicio: item.servicio,
                    nave: item.nave,
                    etd: item.etd,
                    refCliente: item.refCliente,
                    booking: item.booking,
                    montoNeto: item.montoNeto,
                    moneda: item.moneda,
                    extraCostos: item.extraCostos,
                    extraCostosMoneda: item.extraCostosMoneda,
                    subTotal: item.subTotal,
                    numeroLiquidacion: item.nroLiquidacion
                        ? String(item.nroLiquidacion).replace(/^0+/, '')
                        : 'N/A',
                })
            );

            // Actualizar datos y totales
            this.dataSource.data = mappedData;
            this.totalRegistros = response.pagination.total;
            this.totalPaginas = response.pagination.totalPages;

            // Actualizar variables locales
            this.elementosPorPagina = response.pagination.limit;
            this.paginaActual = response.pagination.page;

            // Asegurarse de que el paginador esté sincronizado
            if (this.paginator) {
                this.logger.info(
                    '📊 Estado actual del paginador: ' +
                        JSON.stringify({
                            total: response.pagination.total,
                            pageSize: response.pagination.limit,
                            currentPage: response.pagination.page,
                            pageIndex: response.pagination.page - 1,
                        })
                );

                // Actualizar el paginador solo si los valores son diferentes
                if (this.paginator.length !== response.pagination.total) {
                    this.paginator.length = response.pagination.total;
                }

                if (this.paginator.pageSize !== response.pagination.limit) {
                    this.paginator.pageSize = response.pagination.limit;
                }

                if (this.paginator.pageIndex !== response.pagination.page - 1) {
                    this.paginator.pageIndex = response.pagination.page - 1;
                }
            }

            // Calcular totales para el pie de tabla
            this.calculateTotals(response);

            this.logger.info(
                `✅ Procesados ${mappedData.length} registros de ${response.pagination.total} totales. ` +
                    `Página ${response.pagination.page} de ${response.pagination.totalPages}`
            );
        } catch (error) {
            this.logger.error(
                '❌ Error procesando resultados:',
                error as Error
            );
            throw error;
        }
    }

    /**
     * Calcula totales para mostrar en el pie de tabla
     */
    private calculateTotals(response: OrdenesTransporteResponseDto): void {
        if (!response.totals) return;

        this.totales = {
            count: response.pagination.total,
            totalMontoNeto: response.totals.totalMontoNeto,
            totalExtraCostos: response.totals.totalExtraCostos,
            totalSubTotal: response.totals.totalSubTotal || '—',
            hasMixedCurrencies: response.totals.hasMixedCurrencies || false,
            totalesPorMoneda: (response.totals as any).totalesPorMoneda || [],
        };
    }

    /**
     * Formatea fecha para mostrar en la UI
     */
    private formatDisplayDate(date: Date | string | null): string {
        if (!date) return 'N/A';

        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    /**
     * Formatea montos para mostrar en la tabla
     */
    formatMonto(
        value: number | null | undefined,
        currencyCode: string = 'USD'
    ): string {
        if (value === null || value === undefined) {
            return '-';
        }
        if (value === 0) {
            return '0';
        }

        // Formato: $ X.XXX.000 (con punto como separador de miles y espacio después del $)
        const formatted = Math.round(value).toLocaleString('es-CL', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        // Si la moneda es USD, usamos US$, si no, el código (CLP, UF, etc.)
        const symbol = currencyCode === 'USD' ? '$' : currencyCode || '$';

        // Si la moneda es CLP, generalmente se usa $ también en Chile, pero para distinguir usamos el código
        // El usuario pidió "no mezclar usd con clp", y consistencia.
        // El pipe 'currency' standard usa 'USD' / 'CLP'.
        // Usaremos el código explícito si no es USD para evitar confusión, o $ si es USD.
        // O mejor, replicar comportamiento previo pero dinámico.
        // En el screenshot: "10,00 US$", "2 CLP".

        if (currencyCode === 'USD') return `US$ ${formatted}`;
        if (currencyCode === 'CLP') return `$ ${formatted}`; // Sin sufijo CLP según requerimiento

        // Simplificación para consistencia: "$ X" si es USD/CLP genérico? No, mejor:
        // Si es USD -> US$ X
        // Si es CLP -> $ X (lo usual en chile)
        // Pero el screenshot mostraba "2 CLP".
        // Voy a usar standard angular pipe logic simulada o simplemente:
        return `${currencyCode || '$'} ${formatted}`;
    }

    /**
     * Remueve el sufijo "CLP" de un valor formateado si existe
     * Para usar en las columnas del listado donde no debe aparecer el sufijo
     */
    removeClpSuffix(value: string | null | undefined): string {
        if (!value || typeof value !== 'string') {
            return value || '—';
        }
        // Remover "CLP" del final del string (con espacios antes si existen)
        return value.replace(/\s*CLP\s*$/, '').trim();
    }

    /**
     * Verifica si hay filtros activos
     */
    hasActiveFilters(): boolean {
        return !!(
            this.filtroCliente.value ||
            this.filtroExportador.value ||
            this.filtroNave.value ||
            this.filtroTemporada.value ||
            this.filtroInicio.value ||
            this.filtroFin.value ||
            this.filtroRefCliente.value ||
            this.filtroContenedor.value ||
            this.filtroDus.value ||
            this.filtroNroLiquidacion.value ||
            this.filtroBooking.value ||
            this.filtroPatente.value
        );
    }

    /**
     * Limpia todos los filtros
     */
    limpiarFiltros(): void {
        const currentUser = this.authService.getCurrentUser();
        const isClientUser = currentUser?.roles.includes('client');

        // Si no es usuario cliente, limpiar el filtro de cliente
        if (!isClientUser) {
            this.filtroCliente.setValue('');
        }

        // Limpiar el resto de los filtros
        this.filtroExportador.setValue('');
        this.filtroNave.setValue('');
        this.filtroTemporada.setValue(null);
        this.aplicarTemporadaPorDefecto(true); // Reaplicar temporada por defecto o limpiar

        this.filtroInicio.setValue(null);
        this.filtroFin.setValue(null);
        this.filtroRefCliente.setValue('');
        this.filtroContenedor.setValue('');
        this.filtroDus.setValue('');
        this.filtroNroLiquidacion.setValue('');
        this.filtroBooking.setValue('');
        this.filtroPatente.setValue('');

        this.logger.info(
            '🧹 Filtros limpiados' +
                (isClientUser ? ' (manteniendo filtro de cliente)' : '')
        );

        // Reiniciar paginación
        this.paginaActual = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
            this.paginator.pageSize = 10;
        }

        // Limpiar estado en servicio y URL
        this.navigationService.clearFilters();

        // Buscar con filtros limpios
        this.buscarOrdenes();
    }

    /**
     * Abre el detalle de la orden en una nueva pestaña (filtros en la URL).
     */
    async verOrden(
        orden: OrdenTransporteAgenciaLocal,
        event?: MouseEvent
    ): Promise<void> {
        if (event) {
            event.stopPropagation();
        }

        if (!orden || !orden.nroOt) {
            this.logger.error(
                '❌ Intento de navegar a orden sin ID válido',
                new Error('Orden sin número válido')
            );
            this.snackBar.open('Error: Orden sin número válido', 'Cerrar', {
                duration: 3000,
            });
            return;
        }

        try {
            this.navigationService.openDetailInNewTab(orden.nroOt);
        } catch (error) {
            this.logger.error('❌ Error al abrir detalle', error as Error);
            this.snackBar.open('Error al intentar abrir el detalle', 'Cerrar', {
                duration: 3000,
            });
        }
    }

    /**
     * Exporta a Excel los registros que cumplen con el filtro actual, sin paginación.
     * Formato con logo y colores OPB, igual que los demás módulos.
     */
    async exportarExcel(): Promise<void> {
        if (this.totalRegistros === 0) {
            this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                duration: 3000,
                panelClass: ['warning-snackbar'],
            });
            return;
        }

        this.isGeneratingExcel = true;
        this.logger.info(
            '📊 Exportando a Excel - Órdenes de Transporte (filtro actual, sin paginación)'
        );

        try {
            const filters = this.buildFilters();
            const orderBy = this.sort?.active || 'etd';
            const orderDirection =
                (this.sort?.direction.toUpperCase() as 'ASC' | 'DESC') ||
                'DESC';

            const query: OrdenesTransporteFilterDto = {
                ...filters,
                page: 1,
                limit: 50000,
                orderBy,
                orderDirection,
            };

            const response = await firstValueFrom(
                this.ordenesTransporteService.getOrdenes(query)
            );

            if (!response?.data?.length) {
                this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['warning-snackbar'],
                });
                return;
            }

            await this.generarExcelConEstilos(response.data);
            this.snackBar.open('Excel exportado correctamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error('❌ Error exportando Excel', error as Error);
            this.snackBar.open('Error al exportar Excel', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingExcel = false;
        }
    }

    /**
     * Genera el archivo Excel con estilo OPB (logo y colores), similar a Itinerarios y Liquidaciones.
     */
    private async generarExcelConEstilos(
        data: OrdenesTransporteItemDto[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Órdenes de Transporte');

        const colors = {
            primary: '1e3a8a',
            secondary: '3b82f6',
            dark: '1e293b',
            gray: '64748b',
            light: 'f1f5f9',
            white: 'ffffff',
        };

        let logoArrayBuffer: ArrayBuffer | null = null;
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                logoArrayBuffer = await logoResponse.arrayBuffer();
            }
        } catch {
            this.logger.warn('No se pudo cargar el logo para Excel');
        }

        if (logoArrayBuffer) {
            try {
                const logoId = workbook.addImage({
                    buffer: logoArrayBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            } catch {
                this.logger.warn('Error al agregar logo al Excel');
            }
        }

        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'ÓRDENES DE TRANSPORTE AGENCIA';
        titleCell.font = {
            name: 'Calibri',
            size: 20,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:N1');
        worksheet.getRow(1).height = 40;

        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:N2');
        worksheet.getRow(2).height = 25;

        worksheet.getRow(3).height = 10;

        const columnas = [
            { header: 'Nro O/T', key: 'nroOt', width: 12 },
            { header: 'Nro Liquidación', key: 'nroLiquidacion', width: 14 },
            { header: 'Tipo Embarque', key: 'tipoEmbarque', width: 14 },
            { header: 'Cliente', key: 'cliente', width: 18 },
            { header: 'Exportador', key: 'exportador', width: 18 },
            { header: 'Servicio', key: 'servicio', width: 18 },
            { header: 'Nave', key: 'nave', width: 22 },
            { header: 'ETD', key: 'etd', width: 12 },
            { header: 'Ref. Cliente', key: 'refCliente', width: 14 },
            { header: 'Booking', key: 'booking', width: 16 },
            { header: 'Monto Neto', key: 'montoNeto', width: 14 },
            { header: 'Extra Costos', key: 'extraCostos', width: 14 },
            { header: 'SubTotal', key: 'subTotal', width: 14 },
        ];

        columnas.forEach((col, i) => {
            worksheet.getColumn(i + 1).width = col.width;
        });

        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnas.forEach((col, i) => {
            const cell = worksheet.getCell(4, i + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        data.forEach((item, index) => {
            const rowNum = 5 + index;
            const row = worksheet.getRow(rowNum);
            row.height = 22;

            const fillColor =
                index % 2 === 0
                    ? { argb: 'FFFFFFFF' }
                    : { argb: 'FF' + colors.light };

            const values = [
                item.nroOt ?? '-',
                item.nroLiquidacion ?? '-',
                item.tipoEmbarque ?? '-',
                item.cliente ?? '-',
                item.exportador ?? '-',
                item.servicio ?? '-',
                item.nave ?? '-',
                item.etd ?? '-',
                item.refCliente ?? '-',
                item.booking ?? '-',
                item.montoNeto ?? '-',
                item.extraCostos ?? '-',
                item.subTotal ?? '-',
            ];

            values.forEach((val, colIdx) => {
                const cell = row.getCell(colIdx + 1);
                cell.value = val;
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: typeof val === 'number' ? 'right' : 'left',
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ordenes_transporte_agencia_${
            new Date().toISOString().split('T')[0]
        }.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Exporta el Excel Consolidado: una fila por contenedor, respetando los filtros activos.
     */
    async exportarExcelConsolidado(): Promise<void> {
        if (this.totalRegistros === 0) {
            this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                duration: 3000,
                panelClass: ['warning-snackbar'],
            });
            return;
        }

        this.isGeneratingExcelConsolidado = true;
        this.logger.info(
            '📊 Exportando Excel Consolidado - Órdenes de Transporte (una fila por contenedor)'
        );

        try {
            const filters = this.buildFilters();
            const response = await firstValueFrom(
                this.ordenesTransporteService.getOrdenesConsolidadoExcel(
                    filters
                )
            );

            if (!response?.data?.length) {
                this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['warning-snackbar'],
                });
                return;
            }

            await this.generarExcelConsolidadoConEstilos(response.data);
            this.snackBar.open(
                'Excel Consolidado exportado correctamente',
                'Cerrar',
                {
                    duration: 3000,
                    panelClass: ['success-snackbar'],
                }
            );
        } catch (error) {
            this.logger.error(
                '❌ Error exportando Excel Consolidado',
                error as Error
            );
            this.snackBar.open('Error al exportar Excel Consolidado', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingExcelConsolidado = false;
        }
    }

    /**
     * Genera el archivo Excel Consolidado con estilo OPB (logo y colores), 45 columnas,
     * una fila por contenedor. Mismo patrón que generarExcelConEstilos().
     */
    private async generarExcelConsolidadoConEstilos(
        data: OrdenesTransporteConsolidadoItemDto[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Excel Consolidado');

        const colors = {
            primary: '1e3a8a',
            secondary: '3b82f6',
            dark: '1e293b',
            gray: '64748b',
            light: 'f1f5f9',
            white: 'ffffff',
        };

        let logoArrayBuffer: ArrayBuffer | null = null;
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                logoArrayBuffer = await logoResponse.arrayBuffer();
            }
        } catch {
            this.logger.warn('No se pudo cargar el logo para Excel');
        }

        if (logoArrayBuffer) {
            try {
                const logoId = workbook.addImage({
                    buffer: logoArrayBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            } catch {
                this.logger.warn('Error al agregar logo al Excel');
            }
        }

        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'EXCEL CONSOLIDADO — ÓRDENES DE TRANSPORTE AGENCIA';
        titleCell.font = {
            name: 'Calibri',
            size: 20,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:N1');
        worksheet.getRow(1).height = 40;

        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:N2');
        worksheet.getRow(2).height = 25;

        worksheet.getRow(3).height = 10;

        // 45 columnas, mismo orden y agrupación que el modelo del cliente
        // (identificación, cliente/exportador, nave/naviera/ruta, carga y contenedor,
        // transporte terrestre, trazabilidad con duraciones, Porteo, indicadores de pago, financiero, comentarios)
        const columnas: {
            header: string;
            key: keyof OrdenesTransporteConsolidadoItemDto;
            width: number;
        }[] = [
            { header: 'Nro. O/T', key: 'TransportOrderId', width: 10 },
            { header: 'Nro. Liquidación', key: 'NroLiquidacion', width: 14 },
            { header: 'Nro. Reserva / Booking', key: 'Booking', width: 16 },
            { header: 'Referencia Cliente', key: 'RefCliente', width: 16 },
            { header: 'Cliente', key: 'Cliente', width: 16 },
            { header: 'Exportador', key: 'Exportador', width: 16 },
            { header: 'Nave', key: 'Nave', width: 20 },
            { header: 'Naviera', key: 'CarrierName', width: 20 },
            { header: 'POL', key: 'PortOfLoadingName', width: 14 },
            { header: 'Terminal', key: 'TerminalName', width: 12 },
            { header: 'POD', key: 'PortOfDischargeName', width: 16 },
            { header: 'País Destino', key: 'CountryOfDischargeName', width: 14 },
            { header: 'ETD', key: 'ETD', width: 12 },
            { header: 'Tipo de Embarque', key: 'TipoEmbarque', width: 14 },
            { header: 'Nro. Contenedor', key: 'ContainerNumber', width: 14 },
            { header: 'Sello', key: 'CarrierSealNumber', width: 14 },
            { header: 'S/Conv', key: 'ShipConvenienceIndicatorName', width: 10 },
            { header: 'Cant. Pallets', key: 'QuantityOfPallets', width: 12 },
            { header: 'Tara (KG)', key: 'ContainerTare', width: 12 },
            { header: 'Especies', key: 'MerchandiseName', width: 20 },
            { header: 'Patente Camión', key: 'TruckLicensePlate', width: 14 },
            { header: 'Depósito', key: 'DepotName', width: 18 },
            { header: 'Planta Despacho', key: 'FirstDispatchPlantName', width: 18 },
            { header: 'Localidad Planta Despacho', key: 'FirstDispatchPlantLocationName', width: 20 },
            { header: 'Fecha Citación Planta', key: 'FirstDispatchPlantCitationDate', width: 18 },
            { header: 'Llegada Depósito', key: 'GateInDepotDate', width: 18 },
            { header: 'Salida Depósito', key: 'GateOutDepotDate', width: 18 },
            { header: 'Duración en Depósito (hrs)', key: 'DuracionDepositoHrs', width: 16 },
            { header: 'Entrada Planta Despacho', key: 'GateInDate', width: 18 },
            { header: 'Salida Planta Despacho', key: 'GateOutDate', width: 18 },
            { header: 'Duración en Planta (hrs)', key: 'DuracionPlantaHrs', width: 16 },
            { header: 'Fecha Visación', key: 'SAGCustomsInspectionDate', width: 18 },
            { header: 'Llegada Puerto', key: 'GateInTerminalDate', width: 18 },
            { header: 'Stacking', key: 'GateInStackingDate', width: 18 },
            { header: 'Salida Terminal', key: 'GateOutTerminalDate', width: 18 },
            { header: 'Duración en Terminal (hrs)', key: 'DuracionTerminalHrs', width: 16 },
            { header: 'Porteo', key: 'TypeOfServiceName', width: 12 },
            { header: 'Paga Gate Out', key: 'GateOutDepotPayerIndicatorName', width: 14 },
            { header: 'Pago Gate Out', key: 'GateOutDepotPaymentIndicatorName', width: 14 },
            { header: 'Paga Gate In', key: 'GateInDepotPayerIndicatorName', width: 14 },
            { header: 'Pago Gate In', key: 'GateInDepotPaymentIndicatorName', width: 14 },
            { header: 'Monto Neto', key: 'MontoNeto', width: 14 },
            { header: 'Extra Costos', key: 'ExtraCostos', width: 14 },
            { header: 'SubTotal', key: 'SubTotal', width: 14 },
            { header: 'Comentarios Control Transporte', key: 'RemarksTransportControl', width: 30 },
        ];

        columnas.forEach((col, i) => {
            worksheet.getColumn(i + 1).width = col.width;
        });

        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnas.forEach((col, i) => {
            const cell = worksheet.getCell(4, i + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 11,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        data.forEach((item, index) => {
            const rowNum = 5 + index;
            const row = worksheet.getRow(rowNum);
            row.height = 22;

            const fillColor =
                index % 2 === 0
                    ? { argb: 'FFFFFFFF' }
                    : { argb: 'FF' + colors.light };

            columnas.forEach((col, colIdx) => {
                const val = item[col.key];
                const cell = row.getCell(colIdx + 1);
                cell.value = val === null || val === undefined || val === '' ? '-' : (val as string | number);
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 10,
                    color: { argb: 'FF000000' },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: typeof val === 'number' ? 'right' : 'left',
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ordenes_transporte_consolidado_${
            new Date().toISOString().split('T')[0]
        }.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Manejo de errores
     */
    private handleError(message: string, error: any): void {
        this.hasError = true;
        this.errorMessage = message;
        this.logger.error(message, error);
        this.snackBar.open(message, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar'],
        });
    }

    /**
     * Formatea fecha para envío al backend (YYYY-MM-DD en hora local, evita desfase por timezone)
     */
    private formatDate(date: Date): string {
        return formatDateForApi(date);
    }

    isRowExpanded(orden: OrdenTransporteAgenciaLocal): boolean {
        return this.expandedRowKey === String(orden.nroOt);
    }

    private renderTableRows(): void {
        if (this.table) {
            this.table.renderRows();
        }
    }

    getOrderState(
        orden: OrdenTransporteAgenciaLocal
    ): OrdenTransporteDetalleState | undefined {
        const key = String(orden.nroOt);
        return this.orderDetails[key];
    }

    formatDetailDate(date?: Date | string | null): string {
        if (!date) return 'N/D';
        const parsed = new Date(date);
        if (Number.isNaN(parsed.getTime())) {
            return 'N/D';
        }
        return parsed.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    getComentariosCliente(text?: string | null): string {
        return text?.trim() || 'Sin comentarios';
    }
}
